import { FC } from "react";
import styles from "./List.module.css";
import { Typography } from "ui-kit/Typography";
import classNames from "classnames";
import ListStyle from "./ListStyle";
import ItemText from "./ItemText";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
} from "ui-kit";

export enum ListStyleEnum {
  None = "None",
  Bullet = "Bullet",
  Check = "Check",
  Number = "Number",
  Icon = "Icon",
}

export type ListItem = {
  title: string | null;
  text: string | null;
  icon: string | null;
};

type ListProps = {
  listStyle?: ListStyleEnum;
  collapsible?: boolean;
  items: (ListItem | null)[];
};

const VisibleItem = ({
  index,
  item,
  listStyle,
}: {
  index: number;
  item: ListItem;
  listStyle?: ListStyleEnum;
}) => {
  const { title, text, icon } = item;
  const shouldHaveReducedSpacing = [
    ListStyleEnum.Bullet,
    ListStyleEnum.Check,
  ].includes(listStyle ?? ListStyleEnum.None);
  const shouldHaveSeparator = [
    ListStyleEnum.None,
    ListStyleEnum.Number,
  ].includes(listStyle ?? ListStyleEnum.None);

  return (
    <>
      <li
        className={styles.item}
        style={{
          padding: shouldHaveReducedSpacing ? "4px 0" : "8px 0",
        }}
      >
        <ListStyle listStyle={listStyle} icon={icon} index={index} />
        <Typography className={styles.title}>
          <strong>{title}</strong>
        </Typography>
        {text && (
          <ItemText text={text} noGutter={shouldHaveReducedSpacing} isVisible />
        )}
      </li>
      {shouldHaveSeparator && <div className={styles.separator} />}
    </>
  );
};

const List: FC<ListProps> = ({ listStyle, items, collapsible }) => {
  return collapsible ? (
    <Accordion collapsible multiple>
      {items
        .flatMap((item) => (item ? [item] : []))
        .map((item, index) => (
          <AccordionItem key={item.title}>
            <AccordionTitle
              className={classNames(styles.item, styles.itemCollapsible)}
              icon={
                <ListStyle
                  listStyle={listStyle}
                  icon={item.icon}
                  index={index}
                />
              }
            >
              {item.title}
            </AccordionTitle>
            <AccordionPanel className={styles.textCollapsible}>
              {item.text && <ItemText text={item.text} noGutter isVisible />}
            </AccordionPanel>
          </AccordionItem>
        ))}
    </Accordion>
  ) : (
    <ul className={classNames(styles.list)}>
      {items
        .flatMap((item) => (item ? [item] : []))
        .map((item, index) => (
          <VisibleItem
            key={item.title}
            index={index}
            item={item}
            listStyle={listStyle}
          />
        ))}
    </ul>
  );
};

export default List;
